import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Brain, Layers, Cpu, Cloud, Sparkles, ArrowRight, MessagesSquare,
  Mic, Eye, Network, Lightbulb, Target, ShieldCheck, GraduationCap,
  Workflow, Bell, FileText, Code2, Copy, Check,
} from "lucide-react";
import { useState } from "react";
import PageMeta from "../components/PageMeta";
import {
  VoiceWaveform,
  OCRScanVisual,
  AIInsightCard,
  CVExtractionVisual,
  LiveDashboardPulse,
} from "../components/VisualElements";

const SURFACES = [
  { name: "Inside Rogue", desc: "Drafts emails, researches leads, flags risks, transcribes voice notes, OCRs invoices, and explains data — all within your CRM and ops workflows.", icon: Layers, accent: "text-rogue-cyan" },
  { name: "Inside Nexus", desc: "Called Nexi here. Writes safety rules in plain English, monitors PLC data, and alerts your team before faults happen.", icon: Cpu, accent: "text-nexus-blue" },
  { name: "Inside Cloud", desc: "Coming 2026. Cerebrum will manage your cloud storage, automate backups, and surface usage insights.", icon: Cloud, accent: "text-geminii-dim" },
];

const CAPABILITIES = [
  {
    icon: Mic,
    title: "Speaks · Listens · Transcribes",
    body: "Voice notes, customer calls, site recordings — Cerebrum transcribes everything in 90+ languages, then auto-structures it into job-card notes, CRM updates, or follow-up tasks. ReadAloudButton plays anything back via TTS in your accent.",
    tag: "Voice",
    iconBg: "bg-rogue-cyan/10",
    iconText: "text-rogue-cyan",
    tagText: "text-rogue-cyan",
  },
  {
    icon: Eye,
    title: "Reads anything you scan",
    body: "Documents Hub OCR turns invoices, delivery notes, ID documents, contracts, and handwritten forms into structured data. Auto-matched against suppliers, fuzzy-matched against existing records, and routed to the right module.",
    tag: "Vision",
    iconBg: "bg-cerebrum-violet/10",
    iconText: "text-cerebrum-violet",
    tagText: "text-cerebrum-violet",
  },
  {
    icon: Network,
    title: "Links every module · sees the full business",
    body: "Cerebrum doesn't live inside one module. It connects Sales, Ops, Teams, Projects, Stockroom, Bookkeep, and Support — so when a customer's revenue drops, it knows. When a project slips, it knows. When a job card goes off-pattern, it knows.",
    tag: "Cross-module",
    iconBg: "bg-rogue-emerald/10",
    iconText: "text-rogue-emerald",
    tagText: "text-rogue-emerald",
  },
  {
    icon: Lightbulb,
    title: "Surfaces what you'd otherwise miss",
    body: "Daily Brief in your Notifications Bell. Margin drift on projects. Customers going quiet. Suppliers slipping on lead times. Technicians trending late. Anomalies you'd find weeks later — surfaced this morning.",
    tag: "Insights",
    iconBg: "bg-rogue-amber/10",
    iconText: "text-rogue-amber",
    tagText: "text-rogue-amber",
  },
  {
    icon: Target,
    title: "Recommends · doesn't just report",
    body: "Every insight comes with a next action. 'Reach out to Acacia Farms — they've gone 12 days without a ticket reply.' 'Reorder pump seals — stockroom is 3 days from running out.' 'Reassign JC #A1B2C3 — Marcus is double-booked.'",
    tag: "Recommendations",
    iconBg: "bg-rogue-cyan/10",
    iconText: "text-rogue-cyan",
    tagText: "text-rogue-cyan",
  },
  {
    icon: GraduationCap,
    title: "Coaches your team in real time",
    body: "Rogi coaching mode. Sales reps get suggested talking points mid-call. Technicians get a checklist nudge if a step looks skipped. Managers get a weekly digest of who's improving and where to invest. Every interaction becomes training data.",
    tag: "Coaching",
    iconBg: "bg-cerebrum-violet/10",
    iconText: "text-cerebrum-violet",
    tagText: "text-cerebrum-violet",
  },
  {
    icon: Workflow,
    title: "Learns your business — not just your data",
    body: "Cerebrum studies how YOUR team writes emails, talks to YOUR customers, prices YOUR jobs, runs YOUR projects. After 30 days it doesn't sound like generic AI — it sounds like your business. Thumbs-down once and it rewrites in your voice.",
    tag: "Adaptive",
    iconBg: "bg-rogue-emerald/10",
    iconText: "text-rogue-emerald",
    tagText: "text-rogue-emerald",
  },
  {
    icon: ShieldCheck,
    title: "Tenant-isolated · your data stays yours",
    body: "Every tenant gets a private Cerebrum context. No cross-tenant leakage. No training on your data without explicit consent. SOC2-aligned, role-gated, audit-logged. Owner / admin / manager / rep permissions enforced everywhere.",
    tag: "Privacy",
    iconBg: "bg-white/[0.06]",
    iconText: "text-geminii-muted",
    tagText: "text-geminii-muted",
  },
];

const WORKFLOW_STEPS = [
  { icon: Mic, label: "Hears", detail: "Voice note · call · transcript" },
  { icon: Eye, label: "Sees", detail: "Invoice · CV · photo · form" },
  { icon: Brain, label: "Understands", detail: "Cross-module context · your business" },
  { icon: Lightbulb, label: "Recommends", detail: "Next action · with reasoning" },
  { icon: Bell, label: "Notifies", detail: "Right person · right channel · right moment" },
];

const COMPARISON = [
  { other: "Static AI chatbot bolted on as a sidebar", us: "Embedded across every screen, every module, every workflow" },
  { other: "Trained once on generic SaaS data", us: "Continuously learns your business — your tone, your customers, your pricing" },
  { other: "Answers questions only when asked", us: "Surfaces what you'd otherwise miss — proactively" },
  { other: "Disconnected from the rest of the app", us: "Links Sales · Ops · Teams · Projects · Stockroom · Bookkeep · Support" },
  { other: "Same generic voice for everyone", us: "Rebrand it as your own AI · white-label across Pro & Business tiers" },
];

function EmbedCerebrumSection({ onTrialClick }) {
  const [tab, setTab] = useState("script");
  const [copied, setCopied] = useState(false);

  const snippets = {
    script: `<!-- Embed Cerebrum on any page. One line. -->
<script src="https://embed.geminii-ai.com/cerebrum.js"
        data-tenant="your-tenant-id"
        data-theme="auto"
        data-persona="Aria"
        async></script>`,
    react: `// npm i @geminii/cerebrum-react
import { Cerebrum } from "@geminii/cerebrum-react";

export default function App() {
  return (
    <Cerebrum
      tenantId="your-tenant-id"
      persona="Aria"
      theme="auto"
      context={{ page: "/pricing", user: currentUser }}
    />
  );
}`,
    api: `// Server-to-server. Stream replies from your own backend.
curl https://api.geminii-ai.com/v1/cerebrum/chat/stream \\
  -H "Authorization: Bearer $GEMINII_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "abc-123",
    "message": "What does this customer usually order?",
    "context": { "customer_id": "cust_42" }
  }'`,
  };

  const features = [
    { label: "Your tenant. Your data. Your brand.", desc: "Tenant-isolated knowledge base. Train on your docs, customers, and products — never shared." },
    { label: "One brain, every channel.", desc: "Drops into web, mobile, Slack, WhatsApp, voice — same context everywhere." },
    { label: "Best-model routing.", desc: "Claude Sonnet 4.5 for reasoning, Gemini 3 for vision, Whisper for voice. You don't pick. You don't pay each vendor." },
    { label: "Built-in conversion.", desc: "Lead capture, calendar handoff, and human-handoff baked in. Watch the chat that closed the deal." },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(snippets[tab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <section className="py-14 md:py-20 border-t border-white/[0.04]" data-testid="cerebrum-embed">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
          {/* LEFT — pitch */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cerebrum-violet/10 border border-cerebrum-violet/25 mb-5">
              <Code2 size={12} className="text-cerebrum-violet" />
              <span className="text-[11px] font-mono text-cerebrum-violet uppercase tracking-wider">Cerebrum for developers</span>
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-white mb-4 leading-tight">
              Add the Cerebrum you're <span className="text-cerebrum-violet">talking to right now</span><br className="hidden md:block" /> to your own site.
            </h2>
            <p className="text-geminii-muted leading-relaxed mb-6">
              The chat in the corner of this page? That's Cerebrum embedded in our marketing site. Same brain, page-aware,
              brand-aware, lead-capturing. <span className="text-white">One line of HTML.</span> One npm package. One API key.
              Live in fifteen minutes.
            </p>

            <ul className="space-y-3 mb-8">
              {features.map((f) => (
                <li key={f.label} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-cerebrum-violet/15 border border-cerebrum-violet/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles size={10} className="text-cerebrum-violet" />
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">{f.label}</p>
                    <p className="text-xs text-geminii-muted mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={onTrialClick}
                data-testid="cerebrum-embed-cta-primary"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cerebrum-violet text-white font-semibold text-sm hover:shadow-[0_0_24px_rgba(139,92,246,0.45)] transition-all"
              >
                Get your API key <ArrowRight size={15} />
              </button>
              <Link
                to="/space/rogue/pricing"
                data-testid="cerebrum-embed-cta-pricing"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-cerebrum-violet/50 hover:text-cerebrum-violet transition-all"
              >
                See embed pricing
              </Link>
              <Link
                to="/contact?intent=cerebrum-embed"
                data-testid="cerebrum-embed-cta-sales"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-geminii-muted text-sm hover:text-white transition-colors"
              >
                Talk to engineering
              </Link>
            </div>
          </div>

          {/* RIGHT — code playground */}
          <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0a0b0e] shadow-2xl shadow-black/40">
            {/* Faux IDE header */}
            <div className="flex items-center justify-between bg-white/[0.02] border-b border-white/[0.06] px-4 py-2.5">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-rogue-rose/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-rogue-amber/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-rogue-emerald/50" />
              </div>
              <span className="text-[10px] font-mono text-geminii-dim">cerebrum-quickstart</span>
              <button
                onClick={handleCopy}
                data-testid="cerebrum-embed-copy"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider text-geminii-dim hover:text-cerebrum-violet hover:bg-cerebrum-violet/10 transition"
                aria-label="Copy code"
              >
                {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 px-3 pt-3 border-b border-white/[0.04]">
              {[
                { id: "script", label: "<script>" },
                { id: "react", label: "React" },
                { id: "api", label: "REST API" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`cerebrum-embed-tab-${t.id}`}
                  className={`px-3 py-1.5 text-[11px] font-mono rounded-t-md transition-colors ${
                    tab === t.id
                      ? "bg-white/[0.04] text-cerebrum-violet border-b-2 border-cerebrum-violet -mb-px"
                      : "text-geminii-dim hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Code */}
            <pre className="p-5 text-[12.5px] leading-relaxed font-mono text-geminii-muted overflow-x-auto min-h-[260px]">
              <code data-testid="cerebrum-embed-code">{snippets[tab]}</code>
            </pre>

            <div className="px-5 py-3 border-t border-white/[0.04] flex items-center justify-between text-[10px] font-mono text-geminii-dim">
              <span>↑ paste anywhere · ships in 15 min</span>
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-rogue-emerald animate-pulse" />
                Cerebrum status: live
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CerebrumPage({ onTrialClick }) {
  return (
    <div className="min-h-screen" data-testid="cerebrum-page">
      <PageMeta
        title="Cerebrum AI"
        description="The AI brain of Geminii. Hears, reads, links, recommends, coaches — and learns your business."
        path="/cerebrum"
      />

      {/* ==================== HERO ==================== */}
      <section className="relative pt-28 md:pt-36 pb-14 md:pb-20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(/images/hero-cerebrum.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-geminii-deep/30 via-geminii-deep/70 to-geminii-deep" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-6">
            <img src="/logos/cerebrum/white-blue.png" alt="Geminii Cerebrum" className="h-10 sm:h-12 w-auto" data-testid="cerebrum-hero-logo" />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cerebrum-violet/10 border border-cerebrum-violet/25 mb-5">
            <Sparkles size={12} className="text-cerebrum-violet" />
            <span className="text-[11px] font-mono text-cerebrum-violet uppercase tracking-wider">The brain behind every module</span>
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight max-w-4xl">
            The AI that <span className="text-cerebrum-violet">runs your business</span> with you — not for you.
          </h1>
          <p className="mt-6 text-lg text-geminii-muted leading-relaxed max-w-2xl">
            Cerebrum hears every conversation, reads every document, watches every workflow, and quietly learns
            how your business actually runs. Then it surfaces the one thing you'd have missed — every day, for every user.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={onTrialClick}
              data-testid="cerebrum-cta-trial"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-cerebrum-violet text-white font-semibold text-sm hover:shadow-[0_0_24px_rgba(139,92,246,0.45)] transition-all"
            >
              Try Cerebrum free <ArrowRight size={16} />
            </button>
            <Link
              to="/space/rogue/pricing"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-cerebrum-violet/50 hover:text-cerebrum-violet transition-all"
            >
              See Cerebrum pricing
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== WORKFLOW: HEARS → SEES → UNDERSTANDS → RECOMMENDS → NOTIFIES ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="cerebrum-workflow">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
            How Cerebrum thinks
          </h2>
          <p className="text-geminii-muted mb-12 max-w-xl">
            One brain. Five reflexes. Working across every Rogue module, every minute.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 relative">
            {WORKFLOW_STEPS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative bg-geminii-card border border-cerebrum-violet/15 rounded-xl p-5 text-center"
              >
                <div className="w-10 h-10 mx-auto rounded-lg bg-cerebrum-violet/15 flex items-center justify-center mb-3">
                  <s.icon size={18} className="text-cerebrum-violet" />
                </div>
                <p className="font-display font-bold text-sm text-white mb-1">{s.label}</p>
                <p className="text-[10px] text-geminii-muted leading-relaxed">{s.detail}</p>
                {i < WORKFLOW_STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px bg-cerebrum-violet/20 z-10" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== LIVE VISUAL TRIO: VOICE · OCR · INSIGHTS ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="cerebrum-visuals">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
            Multimodal · in production today
          </h2>
          <p className="text-geminii-muted mb-10 max-w-xl">
            Voice, vision, and structured reasoning — all wired into the same brain.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <VoiceWaveform />
            <OCRScanVisual />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <AIInsightCard />
            <CVExtractionVisual />
          </div>
        </div>
      </section>

      {/* ==================== CAPABILITIES GRID ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="cerebrum-capabilities">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
            What Cerebrum actually does
          </h2>
          <p className="text-geminii-muted mb-12 max-w-xl">
            Eight reflexes that run in the background — turning your operators into your top performers.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CAPABILITIES.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.04 }}
                className="bg-geminii-card border border-white/[0.06] rounded-xl p-6 hover:border-cerebrum-violet/25 transition-colors group"
                data-testid={`cerebrum-capability-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg ${c.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <c.icon size={18} className={c.iconText} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-[10px] font-mono uppercase tracking-wider ${c.tagText} mb-1.5 block`}>
                      {c.tag}
                    </span>
                    <h3 className="font-display font-bold text-base text-white mb-2 leading-snug">
                      {c.title}
                    </h3>
                    <p className="text-sm text-geminii-muted leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== LIVE PLATFORM PULSE ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="cerebrum-pulse">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
            Quietly at work. Always learning.
          </h2>
          <p className="text-geminii-muted mb-10 max-w-xl">
            Every event across every module feeds the same brain. Quietly. Continuously.
          </p>
          <LiveDashboardPulse />
        </div>
      </section>

      {/* ==================== "NOT YOUR AVERAGE AI" COMPARISON ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="cerebrum-comparison">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
            Not another chatbot
          </h2>
          <p className="text-geminii-muted mb-10 max-w-xl">
            We didn't bolt AI onto a sidebar. We built the whole app around it.
          </p>
          <div className="bg-geminii-card border border-white/[0.06] rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-0 border-b border-white/[0.06]">
              <div className="p-5 bg-white/[0.02]">
                <span className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider">Most "AI-powered" SaaS</span>
              </div>
              <div className="w-px bg-white/[0.06]" />
              <div className="p-5 bg-cerebrum-violet/5">
                <span className="text-[10px] font-mono text-cerebrum-violet uppercase tracking-wider">Cerebrum</span>
              </div>
            </div>
            {COMPARISON.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-[1fr_auto_1fr] gap-0 ${i < COMPARISON.length - 1 ? "border-b border-white/[0.04]" : ""}`}
              >
                <div className="p-5">
                  <p className="text-sm text-geminii-muted">{row.other}</p>
                </div>
                <div className="w-px bg-white/[0.06]" />
                <div className="p-5 bg-cerebrum-violet/[0.03]">
                  <p className="text-sm text-white">{row.us}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== ONE BRAIN, EVERY SURFACE ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">One brain. Every surface.</h2>
          <p className="text-geminii-muted mb-12 max-w-xl">
            Cerebrum isn't a separate product. It's the intelligence layer woven into everything Geminii builds.
            Same brain, different skills per surface.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SURFACES.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-geminii-card border border-white/[0.06] rounded-xl p-7"
              >
                <s.icon size={26} className={`${s.accent} mb-4`} />
                <h3 className="font-display font-bold text-base text-white mb-2">{s.name}</h3>
                <p className="text-sm text-geminii-muted leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== WHITE-LABEL ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="cerebrum-whitelabel">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-gradient-to-br from-cerebrum-violet/8 via-cerebrum-violet/4 to-transparent border border-cerebrum-violet/20 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles size={20} className="text-cerebrum-violet" />
                  <span className="text-[10px] font-mono text-cerebrum-violet uppercase tracking-wider">Pro & Business tiers</span>
                </div>
                <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
                  Meet <span className="text-cerebrum-violet">Rogi</span> — or rebrand them as your own
                </h2>
                <p className="text-geminii-muted leading-relaxed mb-4">
                  Out of the box, Cerebrum inside Rogue ships as <span className="text-white">Rogi</span> — a warm, confident persona
                  your team can talk to by name. Upgrade to Pro or Business and you can rename them after your business:
                  Hidro for HidroVerse, Acacia Genie for Acacia Farms, anything your customers will love.
                  Same Cerebrum power, your brand on the badge.
                </p>
                <ul className="space-y-2 text-sm text-geminii-muted">
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-cerebrum-violet mt-2 flex-shrink-0" />
                    <span>Default name: Rogi · custom name, avatar, and personality prompts on Pro+</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-cerebrum-violet mt-2 flex-shrink-0" />
                    <span>White-labelled across every surface — emails, voice, in-app chat</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-cerebrum-violet mt-2 flex-shrink-0" />
                    <span>Tone tuning — formal, friendly, technical, casual</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-3">
                {[
                  { name: "Hidro", client: "HidroVerse (water-systems case study)" },
                  { name: "Acacia Genie", client: "Acacia Farms" },
                  { name: "FastParts Brain", client: "FastParts Industrial" },
                ].map((entry, i) => (
                  <motion.div
                    key={entry.name}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.1 }}
                    className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]"
                  >
                    <div className="w-8 h-8 rounded-full bg-cerebrum-violet/20 flex items-center justify-center flex-shrink-0">
                      <Brain size={14} className="text-cerebrum-violet" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">{entry.name}</p>
                      <p className="text-[10px] text-geminii-dim truncate">{entry.client}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== EMBED CEREBRUM ON YOUR SITE ==================== */}
      <EmbedCerebrumSection onTrialClick={onTrialClick} />

      {/* ==================== TECH NOTE ==================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-start gap-4 max-w-3xl">
            <FileText size={20} className="text-geminii-dim mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-mono text-geminii-dim uppercase tracking-wider mb-2">Under the hood</p>
              <p className="text-sm text-geminii-muted leading-relaxed">
                Cerebrum routes intelligently between best-in-class models — Claude Sonnet 4.5 for reasoning,
                Gemini 3 for multimodal vision, GPT-5.2 for structured generation, OpenAI Whisper for transcription,
                ElevenLabs / OpenAI TTS for voice. You get the right model for the job, every job — without picking,
                paying for, or maintaining any of them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CLOSING CTA ==================== */}
      <section className="py-14 md:py-20 border-t border-white/[0.04]" data-testid="cerebrum-closing">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <MessagesSquare size={36} className="text-cerebrum-violet mx-auto mb-5" />
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white mb-4 leading-tight">
            Stop running your business alone.
          </h2>
          <p className="text-geminii-muted mb-8 max-w-xl mx-auto leading-relaxed">
            Cerebrum is included in the All Rogue Pro and Business plans, or as a standalone add-on from any tier.
            Try it free for 30 days — no credit card.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={onTrialClick}
              data-testid="cerebrum-closing-cta"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-cerebrum-violet text-white font-semibold text-sm hover:shadow-[0_0_28px_rgba(139,92,246,0.5)] transition-all"
            >
              Start free — meet Cerebrum <ArrowRight size={16} />
            </button>
            <Link
              to="/space/rogue/pricing"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-cerebrum-violet/50 hover:text-cerebrum-violet transition-all"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
